import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import TextInput from '../../components/TextInput/TextInput';
import Select from '../../components/Select/Select';
import TextArea from '../../components/TextArea/TextArea';
import PhoneNumberInput from '../../components/PhoneNumberInput/PhoneNumberInput';
import { PayNowAPi } from '../../api/paynow';
import { API_URL } from '../../utils/constants';
import { SEO } from '../../components/SEO';

declare global {
  interface Window {
    Razorpay: any;
    paypal: any;
  }
}

export default function PayNow() {
  const [selectedTab, setSelectedTab] = useState('razorpay');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loadPayPalSdk = (currency: string = 'USD') =>
    new Promise<void>((resolve, reject) => {
      const existingScript = document.getElementById('paypal-sdk');
      if (existingScript) {
        resolve();
        return;
      }

      const clientId =
        process.env.REACT_APP_PAYPAL_CLIENT_ID ||
        'AYDXvqBSqHdprB6oTwBLxgQZ8YaJJ1izt9pfRMlTtZ1wWSU__u-pL7OrtuhxlzAO1Ke5otEDSfGt9H8s';
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}`;
      script.id = 'paypal-sdk';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
      document.body.appendChild(script);
    });
  const payment = async (result: any, gateway: string) => {
    return await fetch(`${API_URL}/b/paymentInitiat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount: result.amount,
        currency: result.currency,
        userId: result._id,
        phone: result.phone,
        gateway
      })
    });
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();
  const [phone, setPhone] = useState('');
  const navigate = useNavigate();
  const processRazorpay = async (data: any) => {
    const result = await PayNowAPi.collectpayment({ ...data, phone });
    if (!result) {
      return;
    }
    const paymentConformation = await payment(result, 'razorpay');
    const { data: paymentData } = await paymentConformation.json();
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: paymentData.order.amount,
      currency: paymentData.order.currency,
      order_id: paymentData.order.id,
      handler: async function () {
        await fetch(`${API_URL}/b/updatepayment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transactionId: paymentData.transction.insertedId
          })
        })
          .then((res) => res.json())
          .then(() => {
            reset();
            setPhone('');
            navigate('/confirmation', { replace: true });
          })
          .catch(() => {});
      },

      prefill: {
        name: data.name || '', // optional
        email: data.email || '', // optional
        contact: phone || '' // this is required for auto-filling the phone
      }
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const processPaypal = async (data: any) => {
    const result = await PayNowAPi.collectpayment({ ...data, phone });
    if (!result) {
      return;
    }
    const paymentConformation = await payment(result, 'paypal');

    const { data: paymentData } = await paymentConformation.json();
    try {
      await loadPayPalSdk(result.currency || 'USD');
      const overlay = document.createElement('div');
      overlay.id = 'paypal-overlay';
      overlay.setAttribute(
        'style',
        'position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;'
      );
      const modal = document.createElement('div');
      modal.setAttribute(
        'style',
        'background:#fff;border-radius:8px;padding:16px;min-width:320px;max-width:420px;width:90%;box-shadow:0 10px 30px rgba(0,0,0,0.2);'
      );
      const closeBtn = document.createElement('button');
      closeBtn.innerText = '×';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.setAttribute(
        'style',
        'float:right;font-size:20px;line-height:20px;padding:4px 8px;margin:-8px -8px 8px 0;background:transparent;border:none;cursor:pointer;'
      );
      const buttonsContainer = document.createElement('div');
      buttonsContainer.id = 'paypal-buttons';
      modal.appendChild(closeBtn);
      modal.appendChild(buttonsContainer);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      const cleanup = () => {
        try {
          document.body.removeChild(overlay);
        } catch (err) {
          /* noop */
        }
      };
      closeBtn.addEventListener('click', cleanup);

      await window.paypal
        .Buttons({
          style: { layout: 'vertical', shape: 'rect', color: 'gold' },
          createOrder: (_d: any, actions: any) => {
            return actions.order.create({
              purchase_units: [
                {
                  amount: {
                    value: String(result.amount),
                    currency_code: result.currency || 'USD'
                  }
                }
              ]
            });
          },
          onApprove: async (_d: any, actions: any) => {
            await actions.order.capture();
            await fetch(`${API_URL}/b/updatepayment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ transactionId: paymentData.transction.insertedId })
            })
              .then((res) => res.json())
              .then(() => {
                cleanup();
                reset();
                setPhone('');
                navigate('/confirmation', { replace: true });
              })
              .catch(() => {
                cleanup();
              });
          },
          onCancel: () => {
            cleanup();
          },
          onError: () => {
            cleanup();
          }
        })
        .render('#paypal-buttons');
    } catch (e) {
      // noop
    }
  };

  const processIndian = async (data: any) => {
    const result = await PayNowAPi.collectpayment({ ...data, phone });
    if (!result) {
      return;
    }
    const paymentConformation = await payment(result, 'indian');
    const { data: paymentData } = await paymentConformation.json();
    await fetch(`${API_URL}/b/updatepayment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId: paymentData.transction.insertedId })
    })
      .then((res) => res.json())
      .then(() => {
        reset();
        setPhone('');
        navigate('/', { replace: true });
      })
      .catch(() => {});
  };

  const processTransferwise = async (data: any) => {
    const result = await PayNowAPi.collectpayment({ ...data, phone });
    if (!result) {
      return;
    }
    const paymentConformation = await payment(result, 'transferwise');
    const { data: paymentData } = await paymentConformation.json();
    await fetch(`${API_URL}/b/updatepayment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId: paymentData.transction.insertedId })
    })
      .then((res) => res.json())
      .then(() => {
        reset();
        setPhone('');
        navigate('/', { replace: true });
      })
      .catch(() => {});
  };

  const onSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      if (selectedTab === 'razorpay') {
        await processRazorpay(data);
        return;
      }
      if (selectedTab === 'paypal') {
        await processPaypal(data);
        return;
      }
      if (selectedTab === 'indian') {
        await processIndian(data);
        return;
      }
      if (selectedTab === 'transferwise') {
        await processTransferwise(data);
        return;
      }
    } catch (e) {
      // noop
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <SEO 
      title="Pay Fees Online - WorldoTutor"
      description="Make secure payment for your tutoring plan. Pay via Razorpay, PayPal, Indian Bank Account, or TransferWise."
      keywords="payment, pay fees, online payment, razorpay, paypal"
      url="/paynow"
      type="website"
      noIndex={true}
    />
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-lg p-8 max-w-2xl w-full">
        <h1 className="text-5xl font-bold mb-6 text-gray-800">Pay Fees Online</h1>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-6">
            <TextInput label="Name of Child" name="childName" register={register} errors={errors} required />
            <TextInput label="Parent name" name="parentName" register={register} errors={errors} required />
            <TextInput label="Teacher name (optional)" name="teacherName" register={register} required={false} />
            <TextInput label="Your Email" name="email" type="email" register={register} errors={errors} required />

            <div className=" w-full flex flex-col">
              <PhoneNumberInput value={phone} containerStyle={{ margin: 0 }} setValue={setPhone} />
            </div>

            <TextInput label="Admission Incharge (optional)" name="admissionIncharge" register={register} />
            <TextInput
              label="No of classes"
              name="numClasses"
              type="number"
              register={register}
              errors={errors}
              required
            />
            <TextInput label="Subject" name="subject" register={register} errors={errors} required />
            <div className="flex space-x-4">
              <Select
                label="Currency"
                name="currency"
                register={register}
                errors={errors}
                required
                options={[
                  { value: 'USD', label: 'US Dollar (USD)' },
                  { value: 'EUR', label: 'Euro (EUR)' },
                  { value: 'GBP', label: 'British Pound (GBP)' },
                  { value: 'AUD', label: 'Australian Dollar (AUD)' },
                  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
                  { value: 'JPY', label: 'Japanese Yen (JPY)' },
                  { value: 'SGD', label: 'Singapore Dollar (SGD)' },
                  { value: 'HKD', label: 'Hong Kong Dollar (HKD)' }
                ]}
              />
              <TextInput label="Amount" name="amount" type="number" register={register} errors={errors} required />
            </div>
            <TextArea label="Remarks" name="remarks" register={register} required={false} />
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Pay Via</h2>
            {/* Row 1: Razorpay, PayPal */}
            <div className="flex border border-orange-500 rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => setSelectedTab('razorpay')}
                className={`flex-1 p-3 ${selectedTab === 'razorpay' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}
              >
                Razorpay
              </button>
              <button
                type="button"
                onClick={() => setSelectedTab('paypal')}
                className={`flex-1 p-3 border-l border-orange-500 ${selectedTab === 'paypal' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}
              >
                PayPal
              </button>
            </div>

            {/* Row 2: Indian Account, TransferWise */}
            <div className="flex border border-orange-500 rounded-md overflow-hidden mt-3">
              <button
                type="button"
                onClick={() => setSelectedTab('indian')}
                className={`flex-1 p-3 ${selectedTab === 'indian' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}
              >
                Indian Account
              </button>
              <button
                type="button"
                onClick={() => setSelectedTab('transferwise')}
                className={`flex-1 p-3 border-l border-orange-500 ${selectedTab === 'transferwise' ? 'bg-orange-500 text-white' : 'bg-white text-black'}`}
              >
                TransferWise
              </button>
            </div>
          </div>

          {selectedTab === 'indian' && (
            <div className="mt-6 p-6 border border-gray-200 rounded-md bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800 mb-4">INDIAN ACCOUNT</h3>
              <p className="mb-6 text-gray-600">Below are the account details:</p>

              <div className="mb-3">
                <h4 className="font-semibold text-gray-700">HDFC BANK</h4>
                <p className="text-gray-600">NAME: WORLDOTUTOR</p>
                <p className="text-gray-600">A/C - 5020-0059-8188-42</p>
                <p className="text-gray-600">IFSC CODE - HDFC0001380</p>
                <p className="text-gray-600">SWIFT CODE: HDFCINBB</p>
                <p className="text-gray-600">BRANCH: PAKHOWAL ROAD LUDHIANA PUNJAB</p>
              </div>
              <div>
                <span className="font-bold">Note: </span>
                <span className="text-red-700">For Indian payments, 18% GST will be applied to the total amount.</span>
              </div>
            </div>
          )}

          {selectedTab === 'transferwise' && (
            <div className="mt-6 p-6 border border-gray-200 rounded-md bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800 mb-4">TRANSFERWISE</h3>
              <p className="mb-6 text-gray-600">Below are the account details:</p>

              <div>
                <h4 className="font-semibold text-gray-700">USA ACCOUNT</h4>
                <p className="font-medium text-gray-700 mt-2">Beneficiary</p>
                <p className="text-gray-600">Beneficiary Name: worldotutor LLC</p>
                <p className="text-gray-600">Account Number - 822000371318</p>
                <p className="text-gray-600">Type of Account - Checking</p>
                <p className="text-gray-600">Wire routing number -026073150</p>
                <p className="text-gray-600">ACH routing number - 026073150</p>
                <p className="text-gray-600">
                  Beneficiary Address - Community Federal Savings Bank 89-16 Jamaica Ave, Woodhaven, NY 11421,United
                  States
                </p>
              </div>
            </div>
          )}

          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-secondary text-white p-3 rounded-full font-semibold  transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>SUBMIT →</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
